import Button from '../components/common/Button';

import SEO from '../components/SEO';

const AdvertisingPage = () => {
  // Color mapping for static Tailwind classes (dynamic classes don't work with purging)
  const colorClasses = {
    primary: {
      bg: 'bg-primary/10',
      text: 'text-primary'
    },
    secondary: {
      bg: 'bg-secondary/10',
      text: 'text-secondary'
    },
    accent: {
      bg: 'bg-accent/10',
      text: 'text-accent'
    }
  };

  const digitalBillboards = [
    {
      icon: 'fa-tv',
      title: 'Outdoor Networks',
      description: 'Premium roadside placements engineered for reach and repeat impressions.',
    },
    {
      icon: 'fa-building',
      title: 'Indoor Screens',
      description: 'High‑dwell environments (malls, airports, events) for targeted influence.',
    },
    {
      icon: 'fa-video',
      title: 'Rich Media',
      description: 'Video, motion graphics, dayparting, and real‑time content triggers.',
    },
    {
      icon: 'fa-cloud',
      title: 'Central Control',
      description: 'Cloud‑managed scheduling and instant creative swaps.',
    },
  ];

  const digitalSignage = [
    {
      icon: 'fa-shopping-cart',
      title: 'Retail Experiences',
      description: 'Drive engagement with interactive product, promo, and queue screens.',
    },
    {
      icon: 'fa-briefcase',
      title: 'Corporate Communication',
      description: 'Branded lobby, meeting‑room, and campus‑wide messaging.',
    },
    {
      icon: 'fa-map-marked-alt',
      title: 'Wayfinding Systems',
      description: 'Intuitive navigation for venues, hospitals, and transport hubs.',
    },
    {
      icon: 'fa-utensils',
      title: 'Menu Boards',
      description: 'Real‑time updates, pricing, and promo playlists for F&B operators.',
    },
  ];

  const whyKommonview = [
    'Premium LED/LCD hardware and calibrated media for vivid, reliable visuals.',
    'Smart playlists, dayparting, and remote updates via cloud CMS.',
    'Performance analytics and transparent reporting on reach and playtime.',
    'Expert installation, SLA‑backed support, and proactive maintenance.',
    'Tailored rollouts that match your brand, audience, and locations.',
  ];

  const workflow = [
    {
      number: '1',
      title: 'Discover',
      description: 'Objectives, audience, and locations aligned to ROI.',
      color: 'primary' as keyof typeof colorClasses,
    },
    {
      number: '2',
      title: 'Design',
      description: 'Creative, layout, and hardware plan for maximum impact.',
      color: 'secondary' as keyof typeof colorClasses,
    },
    {
      number: '3',
      title: 'Deploy',
      description: 'Professional installation, calibration, and QA.',
      color: 'accent' as keyof typeof colorClasses,
    },
    {
      number: '4',
      title: 'Optimize',
      description: 'Managed updates, monitoring, and monthly reporting.',
      color: 'primary' as keyof typeof colorClasses,
    },
  ];

  const quickNav = [
    { label: 'Digital Billboards', href: '#digital-billboards' },
    { label: 'Digital Signage', href: '#digital-signage' },
    { label: 'Why Kommonview', href: '#why-kommonview' },
    { label: 'How We Work', href: '#how-we-work' },
    { label: 'Featured Installations', href: '#featured-installations' },
    { label: 'Gallery', href: '#gallery' },
  ];

  const displayShowcase = [
    {
      title: 'Dynamic Displays',
      description: 'Next-generation digital signage solutions',
    },
    {
      title: 'Interactive Systems',
      description: 'Engage your audience effectively',
    },
    {
      title: 'Future-Ready',
      description: 'Cutting-edge advertising technology',
    },
  ];

  return (
    <div>
      <SEO
        title="Digital Advertising Solutions - Social Media & Google Ads"
        description="High-impact digital advertising campaigns across social media (Facebook, Instagram, LinkedIn) and Google Ads. Drive measurable business outcomes with professional ad management, retargeting, and analytics."
        keywords="digital advertising, social media ads, Google Ads, Facebook advertising, Instagram ads, LinkedIn ads, retargeting, PPC, display advertising, video ads, South Africa digital marketing"
        url="https://kommonview.com/advertising"
      />

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-6" data-aos="slide-right">
              Digital Advertising Solutions
            </h1>
            <p className="text-xl text-gray-100 max-w-4xl mx-auto leading-relaxed mb-8" data-aos="slide-left" data-aos-delay="100">
              Elevate your brand with high-impact digital billboards and intelligent signage, indoors and outdoors.
              We design, deploy, and manage end‑to‑end display networks that deliver attention, clarity, and measurable results.
            </p>

            {/* Quick Navigation */}
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
              <h5 className="text-xl font-bold text-gray-900 mb-4">Quick Navigation</h5>
              <div className="grid grid-cols-2 gap-3 text-left">
                {quickNav.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    className="text-primary hover:text-secondary transition-colors flex items-center"
                  >
                    <i className="fas fa-chevron-right mr-2 text-sm"></i>
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Billboards Section */}
      <section id="digital-billboards" className="section-padding bg-white">
        <div className="container-custom">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-4 text-center">
              Digital Billboards
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {digitalBillboards.map((item, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 mb-4 bg-primary/10 rounded-xl flex items-center justify-center">
                  <i className={`fas ${item.icon} text-3xl text-accent`}></i>
                </div>
                <h3 className="text-xl font-heading font-bold mb-3 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Signage Section */}
      <section id="digital-signage" className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-4 text-center">
              Digital Signage
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {digitalSignage.map((item, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 mb-4 bg-primary/10 rounded-xl flex items-center justify-center">
                  <i className={`fas ${item.icon} text-3xl text-accent`}></i>
                </div>
                <h3 className="text-xl font-heading font-bold mb-3 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Kommonview Section */}
      <section id="why-kommonview" className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-8 text-center">
              Why Kommonview
            </h2>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg">
              <ul className="space-y-4">
                {whyKommonview.map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <i className="fas fa-check-circle text-primary text-2xl mt-1 mr-4 flex-shrink-0"></i>
                    <div>{item}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-12 text-center">
            How We Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflow.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className={`w-16 h-16 ${colorClasses[step.color].bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <span className={`text-3xl font-bold ${colorClasses[step.color].text}`}>{step.number}</span>
                </div>
                <h3 className="text-xl font-heading font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Display Showcase */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-12 text-center">
            Digital Display Showcase
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {displayShowcase.map((item, index) => (
              <div key={index} className="relative overflow-hidden rounded-2xl shadow-lg group">
                <img
                  src={`/images/advertising/${
                    index === 0 ? 'digital-matrix.jpg' :
                    index === 1 ? 'tech-display.jpg' : 'tech-future.jpg'
                  }`}
                  alt={item.title}
                  className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-6">
                  <h5 className="text-white text-xl font-bold mb-2">{item.title}</h5>
                  <p className="text-white/80">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Installations */}
      <section id="featured-installations" className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-12 text-center">
            Featured Installations
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img
              src="/images/advertising/digital-matrix.jpg"
              alt="Digital Matrix Display"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <img
              src="/images/advertising/tech-lights.jpg"
              alt="Technology Light Installation"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <img
              src="/images/advertising/digital-wave.jpg"
              alt="Digital Wave Technology"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <img
              src="/images/advertising/tech-display.jpg"
              alt="Interactive Display System"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-12 text-center">
            Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img
              src="/images/advertising/tech-future.jpg"
              alt="Future Technology Display"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <img
              src="/images/advertising/digital-matrix.jpg"
              alt="Digital Matrix Display"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <img
              src="/images/advertising/tech-lights.jpg"
              alt="Technology Light Installation"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <img
              src="/images/advertising/digital-wave.jpg"
              alt="Digital Wave Technology"
              className="w-full h-48 object-cover rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Ready to Elevate Your Brand?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Contact us today to discuss your digital advertising needs and discover how Kommonview can help your business stand out.
            </p>
            <a href="https://calendly.com/kommonview/30min" target="_blank" rel="noopener noreferrer">
              <Button variant="white" size="lg">
                Get a Free Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdvertisingPage;
